import React, { useState } from 'react';
import styled from 'styled-components';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getHomePathForUser, getUserType, USER_TYPES } from '../utils/userTypeUtils';

/** Plans / billing entry by shell — partners have no subscription catalog CTA. */
const plansPathForUser = (user) => {
  const t = getUserType(user);
  if (t === USER_TYPES.INSTITUTION) return '/institution/plans';
  if (t === USER_TYPES.PARTNER) return '/partner/dashboard';
  if (t === USER_TYPES.ADMIN) return '/admin/subscriptions';
  if (t === USER_TYPES.CONSULTANCY) return '/subscription-billing';
  return '/subscriptions';
};

const plansLabelForUser = (user) => {
  const t = getUserType(user);
  if (t === USER_TYPES.PARTNER) return 'Partner catalog';
  if (t === USER_TYPES.INSTITUTION) return 'Interview plans';
  if (t === USER_TYPES.ADMIN) return 'Plan catalog';
  return 'View plans';
};import { PUBLIC_JOIN_LINKS } from '../constants/publicNav';
import { 
  FiTarget as Target, 
  FiAward as Award, 
  FiUsers as Users, 
  FiCalendar as Calendar, 
  FiFileText as FileText, 
  FiVideo as Video, 
  FiBell as Bell, 
  FiBarChart as ChartBar, 
  FiPlay as Play,
  FiCheckCircle as CheckCircle,
  FiArrowRight as ArrowRight,
  FiZap as Zap,
  FiBookOpen as BookOpen,
  FiBriefcase as Briefcase,
  FiShield as Shield,
  FiClock as Clock
} from 'react-icons/fi';
import BannerHero from '../components/BannerHero';
import TrustPromise from '../components/TrustPromise';
import { heroHome } from '../assets/images';
import InterviewModesCatalog from '../components/InterviewModesCatalog';
import { MODE_NAMES_SHORT } from '../utils/interviewModes';

const HomeContainer = styled.div`
  min-height: 100vh;
  background: #f8fafc;
  color: #0f172a;
`;

const Hero = styled(BannerHero)`
  color: white;
`;

const HeroTitle = styled.h1`
  font-family: 'Fraunces', Georgia, serif;
  font-size: clamp(2.5rem, 8vw, 4rem);
  font-weight: 700;
  margin-bottom: 1.5rem;
  color: #ffffff;
  line-height: 1.15;
  letter-spacing: -0.02em;
  
  @media (max-width: 768px) {
    margin-bottom: 1rem;
  }
  
  @media (max-width: 480px) {
    margin-bottom: 0.75rem;
  }
`;

const HeroSubtitle = styled.p`
  font-size: clamp(1rem, 3vw, 1.4rem);
  margin-bottom: 2.5rem;
  max-width: 700px;
  line-height: 1.6;
  opacity: 0.9;
  padding: 0 1rem;
  
  @media (max-width: 768px) {
    margin-bottom: 2rem;
    padding: 0 0.5rem;
  }
  
  @media (max-width: 480px) {
    margin-bottom: 1.5rem;
    padding: 0 0.25rem;
  }
`;

const CTAButtons = styled.div`
  display: flex;
  gap: 1.5rem;
  margin-bottom: 3rem;
  flex-wrap: wrap;
  justify-content: center;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
    gap: 1rem;
    margin-bottom: 2rem;
  }
  
  @media (max-width: 480px) {
    gap: 0.75rem;
    margin-bottom: 1.5rem;
  }
`;

const Button = styled(Link)`
  padding: clamp(12px, 2.5vw, 15px) clamp(20px, 4vw, 30px);
  border-radius: 50px;
  text-decoration: none;
  font-weight: 600;
  font-size: clamp(0.9rem, 2.5vw, 1.1rem);
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  white-space: normal;
  text-align: center;
  max-width: 100%;
  overflow-wrap: break-word;
  
  svg {
    flex-shrink: 0;
  }
  
  &.primary {
    background: #0F766E;
    color: white;
    box-shadow: 0 4px 15px rgba(13, 148, 136, 0.4);
    
    &:hover {
      transform: translateY(-3px);
      box-shadow: 0 8px 25px rgba(13, 148, 136, 0.6);
    }
    
    @media (max-width: 480px) {
      width: 100%;
      max-width: 320px;
    }
  }
  
  &.secondary {
    background: #ffffff;
    color: #0F766E;
    border: 2px solid #ffffff;
    box-shadow: 0 4px 15px rgba(15, 23, 42, 0.18);
    
    &:hover {
      background: #F0FDFA;
      color: #0F766E;
      transform: translateY(-3px);
    }
    
    @media (max-width: 480px) {
      width: 100%;
      max-width: 320px;
    }
  }

  /* Solid white on dark photo/CTA so consultancy reads as active next to teal candidate */
  &.consultancy {
    background: #ffffff;
    color: #0F766E;
    border: 2px solid #ffffff;
    box-shadow: 0 4px 18px rgba(15, 23, 42, 0.28);
    
    &:hover {
      background: #F0FDFA;
      color: #0F766E;
      transform: translateY(-3px);
      box-shadow: 0 8px 25px rgba(15, 23, 42, 0.35);
    }
    
    @media (max-width: 480px) {
      width: 100%;
      max-width: 320px;
    }
  }

  &.institution {
    background: transparent;
    color: white;
    border: 2px solid white;
    
    &:hover {
      background: rgba(255, 255, 255, 0.12);
      transform: translateY(-3px);
    }
    
    @media (max-width: 480px) {
      width: 100%;
      max-width: 320px;
    }
  }

  &.partner {
    background: transparent;
    color: white;
    border: 2px solid rgba(167, 243, 208, 0.85);
    
    &:hover {
      background: rgba(13, 148, 136, 0.25);
      transform: translateY(-3px);
    }
    
    @media (max-width: 480px) {
      width: 100%;
      max-width: 320px;
    }
  }
  
  @media (max-width: 768px) {
    padding: 12px 24px;
    font-size: 1rem;
  }
  
  @media (max-width: 480px) {
    padding: 10px 20px;
    font-size: 0.9rem;
  }
`;

const StatsSection = styled.div`
  display: flex;
  justify-content: center;
  gap: 3rem;
  margin-bottom: 4rem;
  flex-wrap: wrap;
  
  @media (max-width: 1024px) {
    gap: 2rem;
  }
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1.5rem;
    margin-bottom: 3rem;
  }
  
  @media (max-width: 480px) {
    gap: 1rem;
    margin-bottom: 2rem;
  }
`;

const Stat = styled.div`
  text-align: center;
  min-width: 120px;
  
  @media (max-width: 480px) {
    min-width: 100px;
  }
`;

const StatNumber = styled.div`
  font-size: clamp(2rem, 5vw, 2.5rem);
  font-weight: 700;
  margin-bottom: 0.5rem;
  
  @media (max-width: 768px) {
    margin-bottom: 0.25rem;
  }
`;

const StatLabel = styled.div`
  font-size: clamp(0.8rem, 2vw, 0.9rem);
  opacity: 0.8;
  
  @media (max-width: 480px) {
    font-size: 0.75rem;
  }
`;

const FeaturesSection = styled.div`
  padding: clamp(3rem, 8vw, 6rem) clamp(1rem, 4vw, 2rem);
  background: #f8fafc;
  color: #0f172a;
`;

const SectionTitle = styled.h2`
  font-family: 'Fraunces', Georgia, serif;
  font-size: clamp(2rem, 5vw, 2.5rem);
  text-align: center;
  margin-bottom: clamp(2rem, 5vw, 3rem);
  font-weight: 700;
  color: #0b3b3c;
`;

const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 280px), 1fr));
  gap: clamp(1.5rem, 3vw, 2rem);
  max-width: 1200px;
  margin: 0 auto;
`;

const FeatureCard = styled.div`
  background: #ffffff;
  padding: clamp(1.5rem, 4vw, 2.5rem);
  border-radius: 20px;
  border: 1px solid #e2e8f0;
  transition: all 0.3s ease;
  color: #0f172a;
  
  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 20px 40px rgba(15, 118, 110, 0.12);
  }
`;

const FeatureIcon = styled.div`
  width: clamp(50px, 10vw, 70px);
  height: clamp(50px, 10vw, 70px);
  background: #0F766E;
  border-radius: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1.5rem;
  
  svg {
    color: white;
    width: clamp(20px, 4vw, 30px);
    height: clamp(20px, 4vw, 30px);
  }
`;

const FeatureTitle = styled.h3`
  font-size: clamp(1.2rem, 3vw, 1.4rem);
  margin-bottom: 1rem;
  font-weight: 600;
  color: #0f172a;
`;

const FeatureDescription = styled.p`
  color: #475569;
  line-height: 1.6;
  font-size: clamp(0.9rem, 2.5vw, 1rem);
`;

const ModesSection = styled.div`
  padding: clamp(3rem, 8vw, 5rem) clamp(1rem, 4vw, 2rem);
  background: #ffffff;
  color: #0f172a;
`;

const TestimonialsSection = styled.div`
  padding: 6rem 2rem;
  background: #ecfdf5;
  color: #0f172a;
`;

const TestimonialsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 280px), 1fr));
  gap: 2rem;
  max-width: 1200px;
  margin: 0 auto;
`;

const TestimonialCard = styled.div`
  background: rgba(255, 255, 255, 0.1);
  padding: 2rem;
  border-radius: 15px;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
`;

const TestimonialText = styled.p`
  font-style: italic;
  margin-bottom: 1.5rem;
  line-height: 1.6;
`;

const TestimonialAuthor = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const AuthorAvatar = styled.div`
  width: 50px;
  height: 50px;
  background: #0F766E;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  color: white;
`;

const AuthorInfo = styled.div``;

const AuthorName = styled.div`
  font-weight: 600;
  margin-bottom: 0.25rem;
`;

const AuthorRole = styled.div`
  font-size: 0.9rem;
  opacity: 0.8;
`;

const Stars = styled.div`
  display: flex;
  gap: 0.25rem;
  margin-bottom: 1rem;
`;

const HowItWorksSection = styled.div`
  padding: 6rem 2rem;
  background: #ffffff;
  color: #0f172a;
`;

const StepsSection = styled.div`
  padding: clamp(3rem, 8vw, 6rem) clamp(1rem, 4vw, 2rem);
  background: #ffffff;
  color: #0f172a;
`;

const StepsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 250px), 1fr));
  gap: clamp(1.5rem, 3vw, 2rem);
  max-width: 1000px;
  margin: 0 auto;
  min-width: 0;
  
  @media (max-width: 480px) {
    gap: 1rem;
  }
`;

const StepCard = styled.div`
  text-align: center;
  padding: clamp(1.5rem, 4vw, 2rem);
  
  @media (max-width: 768px) {
    padding: 1.5rem;
  }
  
  @media (max-width: 480px) {
    padding: 1rem;
  }
`;

const StepNumber = styled.div`
  width: clamp(50px, 8vw, 60px);
  height: clamp(50px, 8vw, 60px);
  background: #0F766E;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1.5rem;
  font-weight: 700;
  font-size: clamp(1.2rem, 3vw, 1.5rem);
  
  @media (max-width: 768px) {
    margin-bottom: 1rem;
  }
  
  @media (max-width: 480px) {
    margin-bottom: 0.75rem;
  }
`;

const StepTitle = styled.h3`
  font-size: clamp(1.1rem, 3vw, 1.3rem);
  margin-bottom: 1rem;
  font-weight: 600;
  
  @media (max-width: 768px) {
    margin-bottom: 0.75rem;
  }
  
  @media (max-width: 480px) {
    margin-bottom: 0.5rem;
  }
`;

const StepDescription = styled.p`
  opacity: 0.9;
  line-height: 1.6;
  font-size: clamp(0.9rem, 2.5vw, 1rem);
  
  @media (max-width: 480px) {
    font-size: 0.85rem;
  }
`;

const CTASection = styled.div`
  padding: clamp(3rem, 8vw, 6rem) clamp(1rem, 4vw, 2rem);
  text-align: center;
  color: #fff;
  background-image:
    linear-gradient(rgba(15, 23, 42, 0.78), rgba(15, 23, 42, 0.78)),
    url(${heroHome});
  background-size: cover;
  background-position: center;
`;

const CTATitle = styled.h2`
  font-size: clamp(2rem, 5vw, 2.5rem);
  margin-bottom: 1.5rem;
  font-weight: 700;
  
  @media (max-width: 768px) {
    margin-bottom: 1rem;
  }
  
  @media (max-width: 480px) {
    margin-bottom: 0.75rem;
  }
`;

const CTASubtitle = styled.p`
  font-size: clamp(1rem, 3vw, 1.2rem);
  margin-bottom: 2rem;
  opacity: 0.9;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
  
  @media (max-width: 768px) {
    margin-bottom: 1.5rem;
  }
  
  @media (max-width: 480px) {
    margin-bottom: 1rem;
  }
`;

const JDUploadSection = styled.div`
  padding: clamp(2rem, 6vw, 4rem) clamp(1rem, 4vw, 2rem);
  background: rgba(255, 255, 255, 0.05);
  text-align: center;
  
  @media (max-width: 768px) {
    padding: 2rem 1rem;
  }
  
  @media (max-width: 480px) {
    padding: 1.5rem 0.75rem;
  }
`;

const JDUploadTitle = styled.h2`
  font-size: clamp(2rem, 5vw, 2.5rem);
  margin-bottom: 1.5rem;
  font-weight: 700;
  
  @media (max-width: 768px) {
    margin-bottom: 1rem;
  }
  
  @media (max-width: 480px) {
    margin-bottom: 0.75rem;
  }
`;

const JDUploadSubtitle = styled.p`
  font-size: clamp(1rem, 3vw, 1.2rem);
  margin-bottom: 2rem;
  opacity: 0.9;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
  
  @media (max-width: 768px) {
    margin-bottom: 1.5rem;
  }
  
  @media (max-width: 480px) {
    margin-bottom: 1rem;
  }
`;

const UploadArea = styled.div`
  max-width: 600px;
  margin: 0 auto;
  padding: clamp(1.5rem, 4vw, 2rem);
  background: rgba(255, 255, 255, 0.1);
  border-radius: 15px;
  border: 2px dashed rgba(255, 255, 255, 0.3);
  backdrop-filter: blur(10px);
  transition: all 0.3s ease;
  cursor: pointer;
  
  &:hover {
    border-color: rgba(255, 255, 255, 0.5);
    background: rgba(255, 255, 255, 0.15);
  }
  
  @media (max-width: 768px) {
    padding: 1.5rem;
    border-radius: 12px;
  }
  
  @media (max-width: 480px) {
    padding: 1rem;
    border-radius: 10px;
  }
`;

const UploadInput = styled.input`
  display: none;
`;

const UploadLabel = styled.label`
  display: flex;
  flex-direction: column;
  align-items: center;
  cursor: pointer;
  gap: 1rem;
  
  @media (max-width: 768px) {
    gap: 0.75rem;
  }
  
  @media (max-width: 480px) {
    gap: 0.5rem;
  }
`;

const UploadIcon = styled.div`
  font-size: clamp(2rem, 6vw, 3rem);
  opacity: 0.8;
  
  @media (max-width: 480px) {
    font-size: 2rem;
  }
`;

const UploadText = styled.div`
  font-size: clamp(1rem, 2.5vw, 1.2rem);
  font-weight: 600;
  
  @media (max-width: 768px) {
    font-size: 1rem;
  }
  
  @media (max-width: 480px) {
    font-size: 0.9rem;
  }
`;

const UploadSubtext = styled.div`
  font-size: clamp(0.8rem, 2vw, 0.9rem);
  opacity: 0.8;
  
  @media (max-width: 480px) {
    font-size: 0.75rem;
  }
`;

const UploadButton = styled.button`
  background: #0F766E;
  color: white;
  border: none;
  padding: clamp(10px, 2.5vw, 15px) clamp(20px, 4vw, 30px);
  border-radius: 50px;
  font-weight: 600;
  font-size: clamp(0.9rem, 2.5vw, 1.1rem);
  cursor: pointer;
  transition: all 0.3s ease;
  margin-top: 1rem;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(13, 148, 136, 0.4);
  }
  
  @media (max-width: 768px) {
    margin-top: 0.75rem;
  }
  
  @media (max-width: 480px) {
    margin-top: 0.5rem;
    width: 100%;
    max-width: 280px;
  }
`;

const FilePreview = styled.div`
  margin-top: 1rem;
  padding: 1rem;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const FileInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const RemoveButton = styled.button`
  background: rgba(13, 148, 136, 0.8);
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.9rem;
  
  &:hover {
    background: rgba(13, 148, 136, 1);
  }
`;

const AnalyzeButton = styled.button`
  background: #0F766E;
  color: white;
  border: none;
  padding: 1rem 2rem;
  border-radius: 50px;
  font-weight: 600;
  font-size: 1.1rem;
  cursor: pointer;
  margin-top: 1rem;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(13, 148, 136, 0.6);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const Footer = styled.footer`
  padding: 3rem 2rem;
  background: rgba(0, 0, 0, 0.2);
  text-align: center;
`;

const Home = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [selectedFile, setSelectedFile] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleFileDrop = (event) => {
    event.preventDefault();
    const files = event.dataTransfer.files;
    if (files.length > 0) {
      setSelectedFile(files[0]);
    }
  };

  const handleDragOver = (event) => {
    event.preventDefault();
  };

  const removeFile = () => {
    setSelectedFile(null);
  };

  const analyzeJD = async () => {
    if (!selectedFile) return;
    
    // ... existing analysis flow uses navigate below
    setIsAnalyzing(true);
    
    try {
      if (!user) {
        // If user is not logged in, redirect to registration with JD data
        const formData = new FormData();
        formData.append('jd_file', selectedFile);
        formData.append('jd_data', JSON.stringify({
          filename: selectedFile.name,
          size: selectedFile.size,
          type: selectedFile.type
        }));
        
        // Store JD data in sessionStorage for registration
        sessionStorage.setItem('jd_upload_data', JSON.stringify({
          filename: selectedFile.name,
          size: selectedFile.size,
          type: selectedFile.type
        }));
        
        navigate('/register/candidate');
        return;
      }
      
      // If user is logged in, proceed to JD analysis
      const formData = new FormData();
      formData.append('jd_file', selectedFile);
      
      // Here you would make the API call to analyze the JD
      // For now, redirect to JD management
      navigate('/job-descriptions');
      
    } catch (error) {
      console.error('Error analyzing JD:', error);
    } finally {
      setIsAnalyzing(false);
    }
  };

  const features = [
    {
      icon: <Target size={30} />,
      title: 'Multi-domain interview prep',
      description: 'Practice for software, HR, finance, sales, marketing, healthcare, and more — not just coding roles.'
    },
    {
      icon: <Award size={30} />,
      title: 'Scores that teach',
      description: 'See the latest score per interview mode and what to practice next — same numbers as your dashboard.'
    },
    {
      icon: <BookOpen size={30} />,
      title: 'Guided tutorials',
      description: 'Step-by-step walkthroughs for candidates, consultancies, institutions, and EdTech partners — discoverability, CRM, Talent packs, and weak-topic prep links.'
    },
    {
      icon: <Video size={30} />,
      title: 'Seven interview modes',
      description: `${MODE_NAMES_SHORT}. Pick the format hiring will use, then sit a fuller mock.`,
    },
    {
      icon: <Users size={30} />,
      title: 'Built for consultancies too',
      description: 'CRM pipeline (sourced→placed), Business Intelligence funnel, client companies, invites, and talent unlocks — not an ATS.'
    },
    {
      icon: <Shield size={30} />,
      title: 'Privacy-first talent discovery',
      description: 'Candidates opt in. Contact details stay hidden until a consultancy unlocks a profile.'
    }
  ];

  const highlights = [
    {
      text: 'Practice the screen you will actually face — voice, written, aptitude, coding, typing — not only coding quizzes.',
      label: 'Seven interview modes',
    },
    {
      text: 'Consultancies run CRM Pipeline, Business Intelligence, and Talent Discovery — relationship CRM, not job matching.',
      label: 'Consultancy CRM + BI',
    },
    {
      text: 'Checkout is simulate or Razorpay when keys are set. Simulate does not capture rupees.',
      label: 'Honest billing',
    },
  ];

  const steps = [
    {
      number: "1",
      title: "Upload Job Description",
      description: "Upload a job description to get one recommended interview mode — not job matching."
    },
    {
      number: "2",
      title: "Get Preparation Suggestions",
      description: "Use weak-topic and partner links after practice — not an invented full LMS catalog."
    },
    {
      number: "3",
      title: "Practice the right mode",
      description: "Run a voice or video call, written or MCQ test, aptitude, coding, or typing drill — then a fuller mock."
    },
    {
      number: "4",
      title: "Review & Improve",
      description: "Get detailed feedback with best answer comparisons and improvement areas."
    }
  ];

  return (
    <HomeContainer>
      <Hero imageSrc={heroHome} minHeight="100vh" padding="0 20px">
        <HeroTitle>EnterWeU</HeroTitle>
        <HeroSubtitle>
          A global interview prep and talent platform for every domain — from engineering to HR,
          finance, sales, healthcare, and operations. Practice in seven interview modes. Hire clearer.
        </HeroSubtitle>
        <TrustPromise tone="light" />
        <CTAButtons>
          {user ? (
            <>
              <Button to={getHomePathForUser(user)} className="primary">
                <Zap size={20} />
                Go to dashboard
              </Button>
              <Button
                to={getUserType(user) === USER_TYPES.CONSULTANCY ? '/talent-discovery' : '/interview-modes'}
                className="secondary"
              >
                <Users size={20} />
                {getUserType(user) === USER_TYPES.CONSULTANCY
                  ? 'Discover talent'
                  : 'Practice interview'}
              </Button>
              <Button to="/tutorials" style={{ background: 'transparent', color: 'white', border: '2px solid white' }}>
                <BookOpen size={20} />
                Tutorials
              </Button>
            </>
          ) : (
            <>
              {PUBLIC_JOIN_LINKS.map((link) => (
                <Button
                  key={link.to}
                  to={link.to}
                  className={
                    link.type === 'candidate'
                      ? 'primary'
                      : link.type === 'consultancy'
                        ? 'consultancy'
                        : link.type === 'partner'
                          ? 'partner'
                          : 'institution'
                  }
                >
                  {link.type === 'candidate' ? (
                    <Zap size={20} />
                  ) : link.type === 'consultancy' ? (
                    <Users size={20} />
                  ) : link.type === 'partner' ? (
                    <BookOpen size={20} />
                  ) : (
                    <Briefcase size={20} />
                  )}
                  {link.label}
                </Button>
              ))}
              <Button to="/partners" style={{ background: 'transparent', color: 'white', border: '2px solid white' }}>
                <BookOpen size={20} />
                Browse partners
              </Button>
              <Button to="/tutorials" style={{ background: 'transparent', color: 'white', border: '2px solid white' }}>
                <BookOpen size={20} />
                Start tutorials
              </Button>
            </>
          )}
        </CTAButtons>
        
        <StatsSection>
          <Stat>
            <StatNumber>7</StatNumber>
            <StatLabel>Interview modes</StatLabel>
          </Stat>
          <Stat>
            <StatNumber>8+</StatNumber>
            <StatLabel>Career domains</StatLabel>
          </Stat>
          <Stat>
            <StatNumber>Free</StatNumber>
            <StatLabel>Plan to begin</StatLabel>
          </Stat>
          <Stat>
            <StatNumber>CRM + Talent</StatNumber>
            <StatLabel>For consultancies</StatLabel>
          </Stat>
        </StatsSection>
      </Hero>
      
      <FeaturesSection>
        <SectionTitle>Why Choose EnterWeU?</SectionTitle>
        <FeaturesGrid>
          {features.map((feature, index) => (
            <FeatureCard key={index}>
              <FeatureIcon>
                {feature.icon}
              </FeatureIcon>
              <FeatureTitle>{feature.title}</FeatureTitle>
              <FeatureDescription>{feature.description}</FeatureDescription>
            </FeatureCard>
          ))}
        </FeaturesGrid>
      </FeaturesSection>

      <ModesSection>
        <SectionTitle>How hiring actually screens you</SectionTitle>
        <InterviewModesCatalog ctaTo={user ? '/interview-modes' : '/register/candidate'} ctaLabel={user ? 'Open interview modes' : 'Start with a mode'} />
      </ModesSection>

      <TestimonialsSection>
        <SectionTitle>Built for how hiring really works</SectionTitle>
        <TestimonialsGrid>
          {highlights.map((item, index) => (
            <TestimonialCard key={index}>
              <TestimonialText>{item.text}</TestimonialText>
              <TestimonialAuthor>
                <AuthorInfo>
                  <AuthorName>{item.label}</AuthorName>
                  <AuthorRole>Product fact — not a fabricated review</AuthorRole>
                </AuthorInfo>
              </TestimonialAuthor>
            </TestimonialCard>
          ))}
        </TestimonialsGrid>
      </TestimonialsSection>

      <HowItWorksSection>
        <SectionTitle>How It Works</SectionTitle>
        <StepsGrid>
          {steps.map((step, index) => (
            <StepCard key={index}>
              <StepNumber>{step.number}</StepNumber>
              <StepTitle>{step.title}</StepTitle>
              <StepDescription>{step.description}</StepDescription>
            </StepCard>
          ))}
        </StepsGrid>
      </HowItWorksSection>

      <CTASection>
        <CTATitle>
          {user
            ? getUserType(user) === USER_TYPES.CONSULTANCY
              ? 'Ready to fill your next role?'
              : 'Ready for your next interview?'
            : 'Ready to Ace Your Next Interview?'}
        </CTATitle>
        <CTASubtitle>
          {user
            ? 'Continue where you left off — practice, hire, or review your plan.'
            : 'Join candidates, consultancies, institutions, and EdTech partners who use EnterWeU to practice smarter and hire clearer across every domain.'}
        </CTASubtitle>
        <CTAButtons>
          {user ? (
            <>
              <Button to={getHomePathForUser(user)} className="primary">
                <CheckCircle size={20} />
                Continue in app
              </Button>
              <Button to="/subscriptions" className="secondary">
                <Users size={20} />
                View plans
              </Button>
            </>
          ) : (
            <>
              {PUBLIC_JOIN_LINKS.map((link) => (
                <Button
                  key={`cta-${link.to}`}
                  to={link.to}
                  className={
                    link.type === 'candidate'
                      ? 'primary'
                      : link.type === 'consultancy'
                        ? 'consultancy'
                        : link.type === 'partner'
                          ? 'partner'
                          : 'institution'
                  }
                >
                  {link.type === 'candidate' ? (
                    <CheckCircle size={20} />
                  ) : link.type === 'consultancy' ? (
                    <Users size={20} />
                  ) : link.type === 'partner' ? (
                    <BookOpen size={20} />
                  ) : (
                    <Briefcase size={20} />
                  )}
                  {link.label}
                </Button>
              ))}
              <Button to="/login" style={{ background: 'transparent', color: 'white', border: '2px solid white' }}>
                <ArrowRight size={20} />
                Sign In
              </Button>
            </>
          )}
        </CTAButtons>
        {!user ? (
          <div style={{ marginTop: '1.25rem', display: 'flex', flexWrap: 'wrap', gap: '0.65rem', justifyContent: 'center' }}>
            <Button to="/pricing?audience=consultancy" className="consultancy" style={{ fontSize: '0.95rem' }}>
              Contact sales — consultancy
            </Button>
            <Button to="/pricing?audience=institution" className="institution" style={{ fontSize: '0.95rem' }}>
              Contact sales — institution
            </Button>
            <Button to="/pricing?audience=partner" className="partner" style={{ fontSize: '0.95rem' }}>
              Contact sales — EdTech partner
            </Button>
          </div>
        ) : null}
      </CTASection>

      <JDUploadSection>
        <JDUploadTitle>Upload Your Job Description</JDUploadTitle>
        <JDUploadSubtitle>
          Upload your job description to receive personalized preparation suggestions.
        </JDUploadSubtitle>
        <UploadArea
          onDragOver={handleDragOver}
          onDrop={handleFileDrop}
          onClick={() => document.getElementById('jd-upload').click()}
        >
          <UploadLabel htmlFor="jd-upload">
            <UploadIcon>
              <FileText size={50} />
            </UploadIcon>
            <UploadText>Click to upload your job description (PDF, DOCX, TXT)</UploadText>
            <UploadSubtext>Or drag and drop your file here</UploadSubtext>
          </UploadLabel>
          <UploadInput type="file" id="jd-upload" accept=".pdf,.doc,.docx,.txt" onChange={handleFileSelect} />
          {selectedFile && (
            <FilePreview>
              <FileInfo>
                <FileText size={20} />
                <span>{selectedFile.name}</span>
              </FileInfo>
              <RemoveButton onClick={removeFile}>Remove File</RemoveButton>
            </FilePreview>
          )}
          <AnalyzeButton onClick={analyzeJD} disabled={isAnalyzing}>
            {isAnalyzing ? 'Analyzing...' : 'Analyze Job Description'}
          </AnalyzeButton>
        </UploadArea>
      </JDUploadSection>
    </HomeContainer>
  );
};

export default Home; 